// SPDX-License-Identifier: AGPL-3.0-or-later

// Profile editing: pick the animated effect played over your profile card.
// Saves immediately (like the store's equip button) and lists only effects the user can use.

import * as Modal from '@app/features/app/components/dialogs/Modal';
import Authentication from '@app/features/auth/state/Authentication';
import {useStoreAccess} from '@app/features/store/state/useStoreAccess';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Avatar} from '@app/features/ui/components/Avatar';
import * as UserCommands from '@app/features/user/commands/UserCommands';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/ProfileEffectPicker.module.css';
import {hasProfileFx, ProfileFxLayer} from '@app/features/user/components/ProfileEffectOverlay';
import ProfileEffectCatalog from '@app/features/user/state/ProfileEffectCatalog';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CaretRightIcon, CheckIcon, ProhibitIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

const COPY = {
	title: msg({message: 'Profile effect', comment: 'Section title in profile editing for the profile effect picker.'}),
	description: msg({
		message: 'An animated effect played over your profile card.',
		comment: 'Helper text under the profile effect picker in profile editing.',
	}),
	none: msg({message: 'None', comment: 'Shown when no profile effect is equipped.'}),
	choose: msg({message: 'Choose profile effect', comment: 'Opens the profile effect picker.'}),
	change: msg({message: 'Change profile effect', comment: 'Opens the profile effect picker when one is equipped.'}),
	empty: msg({
		message: 'You do not own any profile effects yet. You can get them in the store.',
		comment: 'Empty state in the profile effect picker.',
	}),
	done: msg({message: 'Done', comment: 'Closes the profile effect picker.'}),
};

/** Miniature version of the user's own profile card with the effect on top. */
function EffectCard({fxKey, image, active}: {fxKey: string | null; image?: string; active: boolean}) {
	const user = Users.currentUser;
	if (!user) return null;
	return (
		<div className={styles.card}>
			<div className={styles.banner} />
			<div className={styles.avatar}>
				<Avatar user={user} size={40} showOffline={false} />
			</div>
			<div className={styles.identity}>
				<span className={styles.name}>{user.displayName || user.username}</span>
				<span className={styles.handle}>{user.username}</span>
			</div>
			{fxKey && hasProfileFx(fxKey) ? (
				<ProfileFxLayer fxKey={fxKey} active={active} />
			) : image ? (
				<img className={styles.still} src={image} alt="" draggable={false} />
			) : null}
		</div>
	);
}

const ProfileEffectPickerModal = observer(function ProfileEffectPickerModal({
	value,
	onChange,
}: {
	value: string | null;
	onChange: (id: string | null) => void;
}) {
	const {i18n} = useLingui();
	const storeAccess = useStoreAccess();
	const [hovered, setHovered] = useState<string | null>(null);
	const [selected, setSelected] = useState<string | null>(value);
	const entries = ProfileEffectCatalog.effects.filter(
		(entry) => entry.accessible !== false && storeAccess.canUse('profile_effect', entry.id),
	);
	const pick = (id: string | null) => {
		setSelected(id);
		onChange(id);
	};
	return (
		<Modal.Root onClose={() => ModalCommands.pop()} size="large">
			<Modal.Header title={i18n._(COPY.title)} />
			<Modal.Content>
				<div className={styles.grid}>
					<button
						type="button"
						className={clsx(styles.option, selected === null && styles.optionSelected)}
						onClick={() => pick(null)}
					>
						<div className={styles.noneTile}>
							<ProhibitIcon size={28} />
						</div>
						<span className={styles.optionName}>{i18n._(COPY.none)}</span>
					</button>
					{entries.map((entry) => (
						<button
							key={entry.id}
							type="button"
							className={clsx(styles.option, selected === entry.id && styles.optionSelected)}
							onMouseEnter={() => setHovered(entry.id)}
							onMouseLeave={() => setHovered(null)}
							onFocus={() => setHovered(entry.id)}
							onClick={() => pick(entry.id)}
						>
							<EffectCard fxKey={entry.fx ?? null} image={entry.image} active={hovered === entry.id} />
							<span className={styles.optionName}>
								{selected === entry.id ? <CheckIcon size={14} weight="bold" /> : null}
								{entry.name}
							</span>
						</button>
					))}
				</div>
				{entries.length === 0 && !storeAccess.loading ? <p className={styles.empty}>{i18n._(COPY.empty)}</p> : null}
			</Modal.Content>
			<Modal.Footer>
				<button type="button" className={styles.done} onClick={() => ModalCommands.pop()}>
					{i18n._(COPY.done)}
				</button>
			</Modal.Footer>
		</Modal.Root>
	);
});

export const ProfileEffectPicker = observer(function ProfileEffectPicker({disabled = false}: {disabled?: boolean}) {
	const {i18n} = useLingui();
	useEffect(() => {
		void ProfileEffectCatalog.load();
	}, []);
	const value = Users.currentUser?.profileEffectId ?? null;
	const selected = ProfileEffectCatalog.getById(value);
	const apply = (id: string | null) => {
		const currentUserId = Authentication.currentUserId ?? Users.currentUserId;
		if (!currentUserId) return;
		void UserCommands.update({profile_effect_id: id}).catch(() => undefined);
	};
	return (
		<div data-flx="user.my-profile-tab.profile-effect-picker">
			<div className={styles.header}>
				<div className={styles.title}>{i18n._(COPY.title)}</div>
				<div className={styles.description}>{i18n._(COPY.description)}</div>
			</div>
			<button
				type="button"
				className={styles.trigger}
				disabled={disabled}
				onClick={() =>
					ModalCommands.push(ModalCommands.modal(() => <ProfileEffectPickerModal value={value} onChange={apply} />))
				}
			>
				<div className={styles.triggerPreview}>
					<EffectCard fxKey={selected?.fx ?? null} image={selected?.image} active={false} />
				</div>
				<div className={styles.triggerName}>{selected?.name ?? i18n._(COPY.none)}</div>
				<span className={styles.triggerAction}>
					{i18n._(selected ? COPY.change : COPY.choose)}
					<CaretRightIcon size={16} weight="bold" />
				</span>
			</button>
		</div>
	);
});
